import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';

export interface Breadcrumb {
  label: string;
  url: string;
}

@Component({
  selector: 'dragonfly-breadcrumb',
  template: `
    @if (breadcrumbs.length > 1) {
      <nav class="breadcrumb-nav">
        @for (crumb of breadcrumbs; track crumb; let last = $last) {
          <span class="breadcrumb-item">
            @if (!last) {
              <a [routerLink]="crumb.url" class="breadcrumb-link">{{ crumb.label }}</a>
            }
            @if (last) {
              <span class="breadcrumb-current">{{ crumb.label }}</span>
            }
            @if (!last) {
              <mat-icon class="breadcrumb-separator">chevron_right</mat-icon>
            }
          </span>
        }
      </nav>
    }
  `,
  styles: [
    `
      .breadcrumb-nav {
        display: flex;
        align-items: center;
        padding: 12px 32px;
        font-family: 'Inter', 'Roboto', sans-serif;
        font-size: 12px;
      }

      .breadcrumb-item {
        display: flex;
        align-items: center;
      }

      .breadcrumb-link {
        color: var(--df-text-muted);
        text-decoration: none;
        font-weight: 500;
        transition: color 150ms ease;

        &:hover {
          color: var(--df-accent);
        }
      }

      .breadcrumb-current {
        color: var(--df-text-primary);
        font-weight: 600;
      }

      .breadcrumb-separator {
        font-size: 14px;
        width: 14px;
        height: 14px;
        color: var(--df-text-muted);
        margin: 0 4px;
        opacity: 0.5;
      }
    `,
  ],
  standalone: false,
})
export class BreadcrumbComponent implements OnDestroy {
  breadcrumbs: Breadcrumb[] = [];
  private subscription: Subscription;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
  ) {
    this.subscription = this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => {
      this.breadcrumbs = this.buildBreadcrumbs(this.route.root);
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private buildBreadcrumbs(route: ActivatedRoute, url = '', crumbs: Breadcrumb[] = []): Breadcrumb[] {
    const children = route.children;
    if (!children.length) return crumbs;

    for (const child of children) {
      const routeURL = child.snapshot.url.map((s) => s.path).join('/');
      if (routeURL) {
        url += `/${routeURL}`;
      }

      const label = child.snapshot.data['breadcrumb'];
      if (label) {
        crumbs.push({ label, url });
      }

      return this.buildBreadcrumbs(child, url, crumbs);
    }

    return crumbs;
  }
}
